<script setup lang="ts">

import {mergeProps, ref, watch} from "vue";
import {useRules} from "vuetify/labs/rules";

const rules = useRules()

interface Props {
    comment: string | null
}

const props = defineProps<Props>();

const emit = defineEmits<{ submit: [string] }>();

const value = ref<boolean>(false);
const form = ref<HTMLFormElement>()
const valid = ref<boolean>(false);
const comment = ref<string>(props.comment ?? '');
const submit = async () => {
    const {valid} = await form.value?.validate()

    if (!valid) {
        return;
    }

    emit('submit', comment.value as string);
    value.value = false;
    form.value?.resetValidation();
}

const closeDialog = () => {
    value.value = false;
    comment.value = props.comment ?? '';
}

watch(value, (value) => {
    if (value) {
        form.value?.resetValidation();
        comment.value = props.comment ?? '';
    }
})
</script>

<template>
    <v-dialog
        v-model="value"
        max-width="500"
        scrim="blue-grey-darken-4"
        opacity="0.5"
        transition="dialog-top-transition"
    >
        <template #activator="{ props: bind }">
            <v-tooltip
                location="top"
                content-class="bg-black border-sm text-tooltip"
                max-width="200"
            >
                <template #activator="{ props: sub }">
                    <v-icon-btn
                        v-bind="mergeProps(bind, sub)"
                        :icon="props.comment ? 'mdi-comment-edit-outline' : 'mdi-comment-outline'"
                        size="small"
                        color="transparent"
                        rounded="lg"
                    />
                </template>
                {{ props.comment ? 'Edit comment' : 'Add comment' }}
            </v-tooltip>
        </template>
        <template #default>
            <v-card
                class="pa-1"
                rounded="lg"
            >
                <v-form
                    ref="form"
                    v-model="valid"
                    @submit.prevent="submit"
                >
                    <v-card-title class="font-weight-bold">
                        Comment
                        <v-btn
                            :icon="true"
                            color="base"
                            variant="plain"
                            rounded="lg"
                            class="float-end"
                            size="32"
                            @click="value = false"
                        >
                            <v-icon size="20">
                                mdi-close
                            </v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="text-body-2 px-4 py-0">
                        <p class="mb-5">
                            Write a comment to provide additional context or information...
                        </p>
                        <v-textarea
                            v-model="comment"
                            density="compact"
                            type="text"
                            name="comment"
                            rows="1"
                            :rules="[rules.required(), rules.pattern(/^(?!\s*$).+/)]"
                            :hide-details="valid"
                            auto-grow
                        />
                    </v-card-text>
                    <v-card-actions class="pt-0 mt-5">
                        <v-spacer />
                        <v-btn
                            color="white"
                            variant="flat"
                            border="sm"
                            type="button"
                            @click="closeDialog"
                        >
                            <span class="font-weight-regular">
                                Cancel
                            </span>
                        </v-btn>
                        <v-btn
                            variant="flat"
                            border="sm"
                            type="submit"
                        >
                            <span class="font-weight-regular">
                                Save comment
                            </span>
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </template>
    </v-dialog>
</template>

<style scoped>

</style>
